import type { FieldValues } from "react-hook-form";
import type {
    FieldStateHelperAPI,
    FormikFieldStateHelperParams,
    HookFormFieldStateHelperParams,
} from "./fieldStateHelper.types";
import { formikFieldStateHelper } from "./formikHelper";
import { hookFormFieldStateHelper } from "./hookFormHelper";

export type FieldStateHelperParams<FormType extends FieldValues> =
    | FormikFieldStateHelperParams<FormType>
    | HookFormFieldStateHelperParams<FormType>;

/**
 * Type guard for FormikFieldStateHelperParams
 * @param props - The props to check
 * @returns True if the props are FormikFieldStateHelperParams, false otherwise
 */
export const isFormikHelper = (
    props: FieldStateHelperParams<FieldValues>
): props is FormikFieldStateHelperParams<FieldValues> => {
    return !("type" in props) || props.type === "formik";
};

/**
 * Type guard for HookFormFieldStateHelperParams
 * @param props - The props to check
 * @returns True if the props are HookFormFieldStateHelperParams, false otherwise
 */
export const isHookFormHelper = (
    props: FieldStateHelperParams<FieldValues>
): props is HookFormFieldStateHelperParams<FieldValues> => {
    return props.type === "hook-form";
};

export const fieldStateHelper = <FormType extends FieldValues>(
    props: FieldStateHelperParams<FormType>
): FieldStateHelperAPI => {
    if (isFormikHelper(props)) {
        return formikFieldStateHelper<FormType>(props);
    }

    if (isHookFormHelper(props)) {
        return hookFormFieldStateHelper<FormType>(props);
    }

    // Exhaustive check - should never reach here with correct types
    throw new Error("Invalid field state helper type");
};
